import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  ILocationComponent, 
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';
import { MapPin, Navigation, X, Loader2 } from 'lucide-react';

interface DfFormLocationProps {
  id: string;
  properties: ILocationComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: ILocationData | null;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
}

interface ILocationData {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp?: number;
  address?: string;
  placeName?: string;
  city?: string;
  country?: string;
}

interface IMapMarker {
  lat: number;
  lng: number;
  isCurrentLocation?: boolean;
}

export const DfFormLocation: React.FC<DfFormLocationProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = null,
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'test',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false
}) => {
  const [locationData, setLocationData] = useState<ILocationData | null>(formValue);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [showMap, setShowMap] = useState<boolean>(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 40.7128, lng: -74.0060 });
  const [mapMarker, setMapMarker] = useState<IMapMarker | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState<boolean>(false);

  // Update location data when formValue prop changes
  useEffect(() => {
    if (formValue) {
      setLocationData(formValue);
      // Update map marker if location data exists
      if (formValue.latitude && formValue.longitude) {
        setMapMarker({
          lat: formValue.latitude,
          lng: formValue.longitude,
          isCurrentLocation: false
        });
        setMapCenter({ lat: formValue.latitude, lng: formValue.longitude });
      }
    } else {
      setLocationData(null);
      setMapMarker(null);
    }
  }, [formValue]);

  // Initialize map with default center (New York)
  useEffect(() => {
    if (showMap && mapCenter.lat === 40.7128 && mapCenter.lng === -74.0060) {
      // Map is already initialized with default center
    }
  }, [showMap, mapCenter]);

  // Close map and reset map-related state when component becomes disabled (e.g., switching to edit mode)
  useEffect(() => {
    if (readonly || disabled) {
      // Close map if component is disabled/readonly (edit/preview mode)
      if (showMap) {
        setShowMap(false);
      }
      // Reset search query and dragging state
      if (searchQuery) {
        setSearchQuery('');
      }
      if (isDragging) {
        setIsDragging(false);
        setDragStart(null);
      }
    }
  }, [readonly, disabled]);

  // Reverse geocoding to get address details
  const reverseGeocode = useCallback(async (lat: number, lng: number) => {
    setIsReverseGeocoding(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`
      );
      const data = await response.json();
      
      if (data && data.address) {
        const address = data.address;
        return {
          placeName: data.display_name || '',
          city: address.city || address.town || address.village || address.municipality || '',
          country: address.country || ''
        };
      }
    } catch (err) {
      console.error('Reverse geocoding failed:', err);
    } finally {
      setIsReverseGeocoding(false);
    }
    return { placeName: '', city: '', country: '' };
  }, []);

  // Update location data with reverse geocoding
  const updateLocationWithAddress = useCallback(async (lat: number, lng: number, existingData?: Partial<ILocationData>) => {
    const addressData = await reverseGeocode(lat, lng);
    const location: ILocationData = {
      latitude: lat,
      longitude: lng,
      timestamp: Date.now(),
      placeName: addressData.placeName,
      city: addressData.city,
      country: addressData.country,
      address: addressData.placeName,
      ...existingData
    };
    
    setLocationData(location);
    setMapMarker({
      lat: lat,
      lng: lng,
      isCurrentLocation: false
    });
    setIsTouched(true);

    if (onValueChange) {
      const change: IFormControlChange = {
        id,
        value: location,
        isValid: true,
        errors: {},
      };
      onValueChange(change);
    }
  }, [reverseGeocode, id, onValueChange]);

  // Get current location using geolocation API
  const getCurrentLocation = useCallback(() => {
    if (readonly || disabled) return;

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }

    setIsLoading(true);
    setError('');

    const options: PositionOptions = {
      enableHighAccuracy: properties.validation?.enableHighAccuracy ?? true,
      timeout: properties.validation?.timeout ?? 10000,
      maximumAge: properties.validation?.maximumAge ?? 300000 // 5 minutes
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setMapMarker({
          lat: lat,
          lng: lng,
          isCurrentLocation: true
        });
        setMapCenter({ lat, lng });
        setIsTouched(true);
        
        // Update location with reverse geocoding
        await updateLocationWithAddress(lat, lng, {
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        });
        
        setIsLoading(false);
      },
      (error) => {
        setIsLoading(false);
        let errorMessage = 'Unable to retrieve your location.';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }
        
        setError(errorMessage);
      },
      options
    );
  }, [readonly, disabled, id, onValueChange, properties]);

  // Clear location data
  const clearLocation = useCallback(() => {
    if (readonly || disabled) return;

    setLocationData(null);
    setMapMarker(null);
    setError('');
    setIsTouched(true);

    if (onValueChange) {
      const change: IFormControlChange = {
        id,
        value: null,
        isValid: true,
        errors: {},
      };
      onValueChange(change);
    }
  }, [readonly, disabled, id, onValueChange]);

  // Toggle map visibility
  const toggleMap = useCallback(() => {
    if (readonly || disabled) return;
    setShowMap(!showMap);
  }, [readonly, disabled, showMap]);

  // Convert pixel coordinates to lat/lng using proper Web Mercator projection
  const pixelToLatLng = useCallback((x: number, y: number, center: { lat: number; lng: number }, zoom: number, width: number, height: number) => {
    // Calculate pixel offset from center
    const offsetX = x - width / 2;
    const offsetY = y - height / 2;
    
    // Convert to meters (approximate)
    const metersPerPixel = (156543.03392 * Math.cos(center.lat * Math.PI / 180)) / Math.pow(2, zoom);
    const metersX = offsetX * metersPerPixel;
    const metersY = offsetY * metersPerPixel;
    
    // Convert meters to lat/lng
    const lat = center.lat + (metersY / 111320);
    const lng = center.lng + (metersX / (111320 * Math.cos(center.lat * Math.PI / 180)));
    
    return { lat, lng };
  }, []);

  // Handle map mouse down (start drag)
  const handleMapMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (readonly || disabled || !mapRef.current) return;
    if (event.button !== 0) return; // Only handle left mouse button
    
    setIsDragging(true);
    setDragStart({ x: event.clientX, y: event.clientY });
    event.preventDefault();
  }, [readonly, disabled]);

  // Handle map mouse move (during drag)
  const handleMapMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStart || !mapRef.current) return;
    
    const deltaX = event.clientX - dragStart.x;
    const deltaY = event.clientY - dragStart.y;
    
    // Convert pixel offset to lat/lng offset
    const metersPerPixel = (156543.03392 * Math.cos(mapCenter.lat * Math.PI / 180)) / Math.pow(2, mapZoom);
    const latOffset = -(deltaY * metersPerPixel) / 111320;
    const lngOffset = (deltaX * metersPerPixel) / (111320 * Math.cos(mapCenter.lat * Math.PI / 180));
    
    setMapCenter({
      lat: mapCenter.lat + latOffset,
      lng: mapCenter.lng + lngOffset
    });
    
    setDragStart({ x: event.clientX, y: event.clientY });
  }, [isDragging, dragStart, mapCenter, mapZoom]);

  // Handle map mouse up (end drag or click)
  const handleMapMouseUp = useCallback(async (event: React.MouseEvent<HTMLDivElement>) => {
    if (!mapRef.current) return;
    
    if (isDragging && dragStart) {
      // Check if it was a drag (moved more than 5 pixels)
      const deltaX = Math.abs(event.clientX - dragStart.x);
      const deltaY = Math.abs(event.clientY - dragStart.y);
      
      if (deltaX > 5 || deltaY > 5) {
        // It was a drag, just update the center
        setIsDragging(false);
        setDragStart(null);
        return;
      }
    }
    
    // It was a click, select location
    if (readonly || disabled) {
      setIsDragging(false);
      setDragStart(null);
      return;
    }
    
    const rect = mapRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    const { lat, lng } = pixelToLatLng(x, y, mapCenter, mapZoom, rect.width, rect.height);
    
    // Update location with reverse geocoding
    await updateLocationWithAddress(lat, lng);
    
    setIsDragging(false);
    setDragStart(null);
  }, [readonly, disabled, mapRef, mapCenter, mapZoom, pixelToLatLng, updateLocationWithAddress, isDragging, dragStart]);

  // Search for location using Nominatim API (OpenStreetMap)
  const searchLocation = useCallback(async (query: string) => {
    if (!query.trim() || readonly || disabled) return;

    setIsSearching(true);
    setError('');

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`
      );
      const results = await response.json();

      if (results && results.length > 0) {
        const firstResult = results[0];
        const lat = parseFloat(firstResult.lat);
        const lng = parseFloat(firstResult.lon);

        // Extract address components
        const address = firstResult.address || {};
        const location: ILocationData = {
          latitude: lat,
          longitude: lng,
          address: firstResult.display_name,
          placeName: firstResult.display_name,
          city: address.city || address.town || address.village || address.municipality || '',
          country: address.country || '',
          timestamp: Date.now()
        };

        setLocationData(location);
        setMapMarker({
          lat: lat,
          lng: lng,
          isCurrentLocation: false
        });
        setMapCenter({ lat, lng });
        setIsTouched(true);

        if (onValueChange) {
          const change: IFormControlChange = {
            id,
            value: location,
            isValid: true,
            errors: {},
          };
          onValueChange(change);
        }
      } else {
        setError('No locations found for your search.');
      }
    } catch (err) {
      setError('Failed to search for location. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [readonly, disabled, id, onValueChange]);

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Handle search form submission
  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (searchQuery.trim()) {
      searchLocation(searchQuery);
    }
  };

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsTouched(true);
    
    if (onBlur) {
      onBlur();
    }
  };

  // Format coordinates for display
  const formatCoordinates = (lat: number | undefined, lng: number | undefined): string => {
    if (lat === undefined || lng === undefined || isNaN(lat) || isNaN(lng)) {
      return '';
    }
    return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
  };

  // Format accuracy for display
  const formatAccuracy = (accuracy?: number): string => {
    if (!accuracy) return '';
    return `±${Math.round(accuracy)}m`;
  };

  const shouldDisable = disabled || readonly || properties?.validation?.readonly;
  const label = properties?.basic?.label || 'Location';
  const description = properties?.basic?.description || '';

  return (
    <div className={`form-group ${hideLabel ? 'no-label' : ''} ${className}`}>
      {!hideLabel && (
        <label htmlFor={id} className="form-label">
          {label}
          {properties?.validation?.required && <span className="required-indicator">*</span>}
        </label>
      )}

      {description && (
        <div className="form-group-description">
          {description}
        </div>
      )}

      <div className="location-wrapper">
        {/* Hide buttons when readonly and location data exists (submission view) */}
        {!(readonly && locationData) && (
          <div className="location-controls">
            <button
              type="button"
              className={`location-btn ${isLoading ? 'loading' : ''}`}
              onClick={getCurrentLocation}
              disabled={shouldDisable || isLoading}
              onFocus={handleFocus}
              onBlur={handleBlur}
              tabIndex={shouldDisable ? -1 : 0}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="loading-spinner" />
                  Getting Location...
                </>
              ) : (
                <>
                  <Navigation size={16} />
                  Get Current Location
                </>
              )}
            </button>

            <button
              type="button"
              className="location-btn secondary"
              onClick={toggleMap}
              disabled={shouldDisable}
              onFocus={handleFocus}
              onBlur={handleBlur}
              tabIndex={shouldDisable ? -1 : 0}
            >
              <MapPin size={16} />
              {showMap ? 'Hide Map' : 'Choose on Map'}
            </button>
          </div>
        )}

        {locationData && (
          <div className="location-display">
            {/* Hide clear button when readonly (submission view) */}
            {!readonly && (
              <button
                type="button"
                className="location-clear-btn"
                onClick={clearLocation}
                disabled={shouldDisable}
                title="Clear location"
              >
                <X size={16} />
              </button>
            )}
            {locationData.placeName && (
              <div className="location-place-name">
                <strong>Place:</strong> {locationData.placeName}
              </div>
            )}
            {(locationData.city || locationData.country) && (
              <div className="location-address">
                <strong>Address:</strong> {[locationData.city, locationData.country].filter(Boolean).join(', ')}
              </div>
            )}
            <div className="location-coordinates">
              <strong>Coordinates:</strong> {formatCoordinates(locationData.latitude, locationData.longitude)}
            </div>
            {locationData.accuracy && (
              <div className="location-accuracy">
                <strong>Accuracy:</strong> {formatAccuracy(locationData.accuracy)}
              </div>
            )}
            {locationData.timestamp && (
              <div className="location-timestamp">
                <strong>Captured:</strong> {new Date(locationData.timestamp).toLocaleString()}
              </div>
            )}
          </div>
        )}

        {/* Hide map when readonly (submission view) */}
        {showMap && !readonly && (
          <div className="location-map-container">
            <div className="location-map-header">
              <h4>Select Location on Map</h4>
              <p>Search for a place or click on the map to select a location</p>
            </div>
            
            {/* Search functionality */}
            <div className="location-search">
              <form onSubmit={handleSearchSubmit} className="search-form">
                <div className="search-input-group">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search for a place (e.g., New York, Paris, Tokyo)"
                    className="search-input"
                    disabled={shouldDisable || isSearching}
                  />
                  <button
                    type="submit"
                    className="search-btn"
                    disabled={shouldDisable || isSearching || !searchQuery.trim()}
                  >
                    {isSearching ? (
                      <Loader2 size={16} className="loading-spinner" />
                    ) : (
                      'Search'
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Map container */}
            <div 
              ref={mapRef}
              className={`location-map ${isDragging ? 'dragging' : ''}`}
              onMouseDown={handleMapMouseDown}
              onMouseMove={handleMapMouseMove}
              onMouseUp={handleMapMouseUp}
              onMouseLeave={() => {
                setIsDragging(false);
                setDragStart(null);
              }}
              style={{
                position: 'relative',
                height: '400px',
                border: '2px solid var(--df-color-fb-border)',
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: isDragging ? 'grabbing' : 'grab',
                background: `url(https://tile.openstreetmap.org/${mapZoom}/${Math.floor((mapCenter.lng + 180) / 360 * Math.pow(2, mapZoom))}/${Math.floor((1 - Math.log(Math.tan(mapCenter.lat * Math.PI / 180) + 1 / Math.cos(mapCenter.lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, mapZoom))}.png) center/cover no-repeat`,
                userSelect: 'none'
              }}
            >
              {/* Map marker */}
              {mapMarker && (
                <div
                  className={`map-marker ${mapMarker.isCurrentLocation ? 'current-location' : 'selected-location'}`}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -100%)',
                    zIndex: 10
                  }}
                >
                  <MapPin size={24} />
                </div>
              )}
              
              {/* Map center indicator */}
              <div
                className="map-center-indicator"
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '4px',
                  height: '4px',
                  backgroundColor: 'var(--df-color-primary)',
                  borderRadius: '50%',
                  zIndex: 5
                }}
              />

              {/* Map controls */}
              <div className="map-controls">
                <button
                  type="button"
                  className="map-zoom-btn"
                  onClick={() => setMapZoom(prev => Math.min(18, prev + 1))}
                  disabled={mapZoom >= 18}
                >
                  +
                </button>
                <button
                  type="button"
                  className="map-zoom-btn"
                  onClick={() => setMapZoom(prev => Math.max(1, prev - 1))}
                  disabled={mapZoom <= 1}
                >
                  −
                </button>
              </div>
            </div>
            
            <div className="location-map-instructions">
              <p><strong>Instructions:</strong></p>
              <ul>
                <li>Search for a place using the search box above</li>
                <li>Click anywhere on the map to select a location</li>
                <li>Drag the map to pan around</li>
                <li>Use the zoom buttons (+/−) to zoom in/out</li>
                <li>Use "Get Current Location" to automatically detect your position</li>
              </ul>
            </div>
            
            {isReverseGeocoding && (
              <div className="location-loading">
                <Loader2 size={16} className="loading-spinner" />
                <span>Loading address details...</span>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="location-error">
            {error}
          </div>
        )}
      </div>

      <DfFormErrorMsg
        validationErrors={validationErrors}
        fieldId={id}
        touchedFields={touchedFields}
        formSubmitted={formSubmitted}
        properties={properties}
        localValidation={{ isValid: true, errors: {} }}
        isTouched={isTouched}
        mode={mode}
      />
    </div>
  );
};

export default DfFormLocation;
